import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { soccerPath } from '../utils/paths';
import { mediaUrl, normalizeReferenceLinks, REFERENCE_LINK_PRESETS } from '../utils/blogUtils';
import BlogShareBar from './BlogShareBar';
import './ClubBlog.css';

const ClubBlogEditor = () => {
  const { id: clubId, postId } = useParams();
  const isEdit = Boolean(postId);
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentPostId, setCurrentPostId] = useState(postId || null);
  const [photoFiles, setPhotoFiles] = useState([]);
  const [savedSlug, setSavedSlug] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    body: '',
    coverImageUrl: '',
    photoUrls: [],
    referenceLinks: [],
    status: 'DRAFT',
  });

  const loadPost = useCallback(async () => {
    if (!isEdit || !user?.id) return;
    try {
      setLoading(true);
      const post = await api.getClubPost(clubId, postId, user.id);
      setFormData({
        title: post.title || '',
        excerpt: post.excerpt || '',
        body: post.body || '',
        coverImageUrl: post.coverImageUrl || '',
        photoUrls: post.photoUrls || [],
        referenceLinks: post.referenceLinks?.length ? post.referenceLinks : [],
        status: post.status || 'DRAFT',
      });
      setCurrentPostId(post.id);
      setSavedSlug(post.slug || '');
    } catch (err) {
      setError(err.message || 'Failed to load post');
    } finally {
      setLoading(false);
    }
  }, [clubId, postId, isEdit, user?.id]);

  useEffect(() => {
    loadPost();
  }, [loadPost]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError('');
    setSuccess('');
  };

  const handlePhotoSelect = (e) => {
    setPhotoFiles(Array.from(e.target.files));
  };

  const handleAddReferenceLink = (preset) => {
    setFormData((prev) => ({
      ...prev,
      referenceLinks: [...(prev.referenceLinks || []), { label: preset?.label || '', url: '' }],
    }));
  };

  const handleReferenceLinkChange = (index, field, value) => {
    setFormData((prev) => {
      const links = [...(prev.referenceLinks || [])];
      links[index] = { ...links[index], [field]: value };
      return { ...prev, referenceLinks: links };
    });
  };

  const handleRemoveReferenceLink = (index) => {
    setFormData((prev) => ({
      ...prev,
      referenceLinks: (prev.referenceLinks || []).filter((_, i) => i !== index),
    }));
  };

  const handleRemovePhoto = (photoUrl) => {
    if (!window.confirm('Remove this photo from the post?')) return;
    setFormData((prev) => {
      const photoUrls = (prev.photoUrls || []).filter((url) => url !== photoUrl);
      let coverImageUrl = prev.coverImageUrl;
      if (coverImageUrl === photoUrl) {
        coverImageUrl = photoUrls[0] || '';
      }
      return { ...prev, photoUrls, coverImageUrl };
    });
    setError('');
    setSuccess('');
  };

  const handleSave = async ({ publish = false, unpublish = false } = {}) => {
    if (!user?.id) return;
    if (!formData.title.trim() || !formData.body.trim()) {
      setError('Title and body are required');
      return;
    }

    const nextStatus = unpublish ? 'DRAFT' : publish ? 'PUBLISHED' : formData.status;

    const payload = {
      title: formData.title.trim(),
      excerpt: formData.excerpt.trim(),
      body: formData.body.trim(),
      coverImageUrl: formData.coverImageUrl || null,
      photoUrls: formData.photoUrls,
      referenceLinks: normalizeReferenceLinks(formData.referenceLinks),
      status: nextStatus,
    };

    try {
      setSaving(true);
      setError('');
      let saved;
      if (currentPostId) {
        saved = await api.updateClubPost(clubId, currentPostId, payload, user.id);
      } else {
        saved = await api.createClubPost(clubId, payload, user.id);
        setCurrentPostId(saved.id);
        setSavedSlug(saved.slug || '');
        navigate(soccerPath(`/clubs/${clubId}/admin/blog/${saved.id}/edit`), { replace: true });
      }

      if (photoFiles.length > 0) {
        setUploading(true);
        const urls = await api.uploadClubPostPhotos(clubId, saved.id, photoFiles, user.id);
        setFormData((prev) => ({
          ...prev,
          photoUrls: [...(prev.photoUrls || []), ...urls],
          coverImageUrl: prev.coverImageUrl || urls[0] || prev.coverImageUrl,
        }));
        setPhotoFiles([]);
      }

      if (unpublish) {
        setSuccess('Post unpublished. It is hidden from the public blog.');
      } else if (publish) {
        setSuccess('Post published!');
      } else {
        setSuccess('Post saved.');
      }
      setFormData((prev) => ({ ...prev, status: saved.status || nextStatus }));
      if (saved.slug) {
        setSavedSlug(saved.slug);
      }
    } catch (err) {
      setError(err.message || 'Failed to save post');
    } finally {
      setSaving(false);
      setUploading(false);
    }
  };

  const handleUnpublish = async () => {
    if (!window.confirm('Unpublish this post? It will no longer appear on the public blog.')) {
      return;
    }
    await handleSave({ unpublish: true });
  };

  if (loading) {
    return <div className="loading">Loading post…</div>;
  }

  return (
    <div className="club-blog-editor card">
      <div className="club-blog-editor__header">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => navigate(soccerPath(`/clubs/${clubId}/admin/blog`))}
        >
          ← Back to posts
        </button>
        <h2>{isEdit ? 'Edit blog post' : 'New blog post'}</h2>
        {isEdit && (
          <span className={`club-blog-admin__badge club-blog-admin__badge--${formData.status.toLowerCase()}`}>
            {formData.status}
          </span>
        )}
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <form
        className="club-blog-editor__form"
        onSubmit={(e) => {
          e.preventDefault();
          handleSave();
        }}
      >
        <div className="form-group">
          <label htmlFor="title">Title *</label>
          <input
            id="title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            required
            placeholder="e.g. Columbus Tuskers win tournament"
          />
        </div>

        <div className="form-group">
          <label htmlFor="excerpt">Short excerpt</label>
          <input
            id="excerpt"
            name="excerpt"
            value={formData.excerpt}
            onChange={handleChange}
            placeholder="One-line summary for the blog list"
          />
        </div>

        <div className="form-group">
          <label htmlFor="body">Story *</label>
          <textarea
            id="body"
            name="body"
            value={formData.body}
            onChange={handleChange}
            required
            rows={12}
            placeholder="Write your post. Use blank lines between paragraphs."
          />
        </div>

        <div className="form-group">
          <label>Reference links</label>
          <p className="club-blog-editor__hint">
            Add links to related Facebook posts, Instagram reels, news articles, etc.
          </p>
          <div className="club-blog-editor__reference-presets">
            {REFERENCE_LINK_PRESETS.map((preset) => (
              <button
                key={preset.label}
                type="button"
                className="btn btn-sm btn-secondary"
                onClick={() => handleAddReferenceLink(preset)}
              >
                + {preset.label}
              </button>
            ))}
          </div>
          <div className="club-blog-editor__references">
            {(formData.referenceLinks || []).length === 0 && (
              <p className="club-blog-editor__hint">No reference links yet.</p>
            )}
            {(formData.referenceLinks || []).map((link, index) => (
              <div key={index} className="club-blog-editor__reference-row">
                <input
                  type="text"
                  value={link.label || ''}
                  onChange={(e) => handleReferenceLinkChange(index, 'label', e.target.value)}
                  placeholder="Label (e.g. Facebook)"
                />
                <input
                  type="url"
                  value={link.url || ''}
                  onChange={(e) => handleReferenceLinkChange(index, 'url', e.target.value)}
                  placeholder="https://..."
                />
                <button
                  type="button"
                  className="btn btn-sm btn-danger"
                  onClick={() => handleRemoveReferenceLink(index)}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="photos">Photos</label>
          <input id="photos" type="file" accept="image/*" multiple onChange={handlePhotoSelect} />
          {photoFiles.length > 0 && (
            <p className="club-blog-editor__hint">{photoFiles.length} file(s) ready to upload on save</p>
          )}
        </div>

        {formData.photoUrls.length > 0 && (
          <div className="club-blog-editor__gallery">
            <p>Uploaded photos</p>
            <p className="club-blog-editor__hint">Remove unwanted photos, then save the post to apply changes.</p>
            <ul className="club-blog-editor__photo-list">
              {formData.photoUrls.map((photoUrl) => (
                <li key={photoUrl} className="club-blog-editor__photo-item">
                  <img src={mediaUrl(photoUrl)} alt="" className="club-blog-editor__photo-thumb" />
                  {formData.coverImageUrl === photoUrl && (
                    <span className="club-blog-editor__photo-cover-badge">Cover</span>
                  )}
                  <button
                    type="button"
                    className="btn btn-sm btn-danger club-blog-editor__photo-remove"
                    onClick={() => handleRemovePhoto(photoUrl)}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {formData.coverImageUrl && (
          <div className="form-group">
            <label>Cover image</label>
            <img src={mediaUrl(formData.coverImageUrl)} alt="" className="club-blog-editor__cover-preview" />
          </div>
        )}

        {formData.status === 'PUBLISHED' && savedSlug && (
          <BlogShareBar
            post={{
              slug: savedSlug,
              title: formData.title,
              excerpt: formData.excerpt,
            }}
          />
        )}

        <div className="club-blog-editor__actions">
          {formData.status === 'PUBLISHED' ? (
            <>
              <button type="submit" className="btn btn-primary" disabled={saving || uploading}>
                {uploading ? 'Uploading photos…' : saving ? 'Saving…' : 'Save changes'}
              </button>
              <button
                type="button"
                className="btn btn-warning"
                disabled={saving || uploading}
                onClick={handleUnpublish}
              >
                {saving ? 'Unpublishing…' : 'Unpublish'}
              </button>
            </>
          ) : (
            <>
              <button type="submit" className="btn btn-secondary" disabled={saving || uploading}>
                {saving ? 'Saving…' : 'Save draft'}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={saving || uploading}
                onClick={() => handleSave({ publish: true })}
              >
                {uploading ? 'Uploading photos…' : saving ? 'Publishing…' : 'Publish'}
              </button>
            </>
          )}
        </div>
      </form>
    </div>
  );
};

export default ClubBlogEditor;
